import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, Switch } from 'react-router-dom';
import { SearchBar, BlankFormBox } from '@icoach/documents/components/';
import MembershipTransferInHistory from '@icoach/documents/membership/transferIn/MembershipTransferInHistory';
import MembershipTransferInContent from '@icoach/documents/membership/transferIn/MembershipTransferInContent';
import MembershipTransferInSearchBarTabs from '@icoach/documents/membership/transferIn/MembershipTransferInSearchBarTabs';
import ConfirmTemporaryContractDialog from '@icoach/documents/dialog/ConfirmTemporaryContractDialog';
import { DOC_MEMBERSHIP_TRANSFER_IN_LINK } from '@icoach/documents/membership/staticData';
import { CLUB_DOCUMENTS_ROUTER } from '@icoach/router/routerPath';
import useDocMembershipTransferInApi from '@apis/useDocMembershipTransferInApi';
import PrivateRoute from "@common/components/route/PrivateRoute";
import { Typography, Box } from "@curves/ui-base";
import { ConfirmHealthDeclarationNotifyDialog } from '@icoach/documents/dialog';
import useToggleDialog from '@util/hook/useToggleDialog';
import { isEqual, isEmpty } from '@util/utils';
import useDocHealthDeclarationApi from '@apis/useDocHealthDeclarationApi';
import { QUERY_KEYS, PEOPLE_TYPE, TRANSFERIN_ENTEREDTYPE } from '@icoach/constants/';
import { resolveDefaultTargetType, resolveEnteredTypeByTargetType, isManualWithoutReference } from '@icoach/documents/membership/transferIn/transferInFormRules';

const DIALOGTYPE = {
	TEMPORARY: 'temporary',
	HEALTHDECLARATIONNOTIFY: 'healthDeclarationNotify'
};

const MembershipTransferInPage = ({ path, match, isHistory = false, ...props }) => {
	const { getInitTransferInUnenteredOptionApi, getInitTransferInEnteredOptionApi, getDocTemporaryTransferInContractApi, deleteDocTemporaryTransferInContractApi } = useDocMembershipTransferInApi();
	const history = useHistory();
	const { peopleType: peopleTypeProps, enteredType: enteredTypeProps, page: pageStatus, referenceID: referenceIDProps } = useParams();
	const [options, setOptions] = useState({});
	const { getDocHealthDeclarationIsValidApi } = useDocHealthDeclarationApi();
	const { openTarget, handleOpenDialog, handleCloseDialog } = useToggleDialog();
	const [isMounted, setMounted] = useState(false);
	const searchRef = useRef([]);
	const temporaryIDRef = useRef(0);

	const goToHealthDeclarationPage = target => {
		const { peopleType = peopleTypeProps, referenceID = referenceIDProps } = target;
		const contractID = 0;
		let path = CLUB_DOCUMENTS_ROUTER.HEALTHDECLARATION.GET_CONTRACT_PATH(peopleType, referenceID, contractID);
		path = path + `?${QUERY_KEYS.CONTINUE_DOC}`;
		history.push(path);
	};

	const checkPeopleType = opts => {
		const { targetOptions = [] } = opts;
		const isInclude = targetOptions.some(({ value }) => isEqual(peopleTypeProps, value));
		//初始預設值 尚未移入 10(移入會員) , 已移入 14(已移入會員)
		const nextTargetType = resolveDefaultTargetType(enteredTypeProps);
		if (!peopleTypeProps || !isInclude) {
			history.replace(CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_PEOPLETYPE_PATH(enteredTypeProps, nextTargetType));
		}
	};

	// 搜尋 按鈕
	const handleSearchHistoryClick = referenceID => {
		if (!pageStatus || isEqual(pageStatus, 'history') || isEqual(pageStatus, 'contract')) {
			const { targetType, targetID } = searchRef.current.getResult();
			const rID = referenceID || targetID;
			const path = CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_HISTORY_PATH(enteredTypeProps, targetType, rID);
			history.replace(path);
		}
	};

	// 取消 按鈕 返回總覽頁 移入流程與其他文書不同故邏輯不同
	const handleReturnHistory = (nextTargetType, nextTargetID) => {
		if (nextTargetID !== 0) {
			let enteredType = resolveEnteredTypeByTargetType(nextTargetType);
			const path = CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_HISTORY_PATH(enteredType, nextTargetType, nextTargetID);
			history.replace(path);
		} else {
			handleResetClick(peopleTypeProps);
		}
	};

	// 重設 按鈕
	const handleResetClick = targetType => {
		const path = CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_PEOPLETYPE_PATH(enteredTypeProps, targetType);
		history.replace(path);
	};

	// 新增 按鈕
	const handleCreateClick = async () => {
		const { targetID: referenceID, targetType: peopleType } = searchRef.current.getResult();
		const params = { id: referenceID, type: peopleType };
		if (isManualWithoutReference(peopleType, referenceID) || !(await doCheckHealthDeclarationApi(peopleType, referenceID))) {
			// 前置檢查健康申告書
			handleOpenDialog(DIALOGTYPE.HEALTHDECLARATIONNOTIFY);
		} else {
			doCreateBlankContractApi(params, peopleType, referenceID);
		}
	};

	// 彈出視窗 要刪除暫存資料
	const handleDialogNoClick = () => {
		const params = {
			id: referenceIDProps,
			type: peopleTypeProps
		};
		doDeleteTemporaryDocumentTransferInContractApi(params);
	};

	// 彈出視窗 讀取上次暫存資料繼續編輯
	const handleDialogYseClick = () => {
		if (!temporaryIDRef.current) return;
		handleCloseDialog();
		const contractID = temporaryIDRef.current;
		const path = CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_CONTRACT_PATH(TRANSFERIN_ENTEREDTYPE.UNENTERED, peopleTypeProps, referenceIDProps, contractID);
		history.push(path);
	};

	// 彈出視窗 跳至健康申告書
	const handleGoToHealthDeclarationPage = () => {
		const { targetID, targetType: peopleType } = searchRef.current.getResult();
		const referenceID = isEmpty(targetID) ? 0 : targetID;
		goToHealthDeclarationPage({
			peopleType,
			referenceID
		});
	};

	// 刪除暫存文書 且跳轉到新增空白的移入申請書
	const doDeleteTemporaryDocumentTransferInContractApi = async params => {
		const resp = await deleteDocTemporaryTransferInContractApi(params);
		if (resp) {
			const contractID = 0;
			const path = CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_CONTRACT_PATH(TRANSFERIN_ENTEREDTYPE.UNENTERED, peopleTypeProps, referenceIDProps, contractID);
			history.push(path);
		}
	};

	// 新增空白頁面
	const doCreateBlankContractApi = async (params, peopleType, referenceID) => {
		let temporaryID;
		temporaryIDRef.current = 0;
		temporaryID = await getDocTemporaryTransferInContractApi(params);
		if (temporaryID !== 0 && !!temporaryID) {
			temporaryIDRef.current = temporaryID;
			handleOpenDialog(DIALOGTYPE.TEMPORARY);
		} else {
			const contractID = 0;
			const path = CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_CONTRACT_PATH(TRANSFERIN_ENTEREDTYPE.UNENTERED, peopleType, referenceID, contractID);
			history.push(path);
		}
	};

	// 初始searchbar options
	const doInitOptionsApi = async () => {
		let resp;
		if (isEqual(enteredTypeProps, TRANSFERIN_ENTEREDTYPE.UNENTERED)) {
			resp = await getInitTransferInUnenteredOptionApi();
		} else if (isEqual(enteredTypeProps, TRANSFERIN_ENTEREDTYPE.ENTERED)) {
			resp = await getInitTransferInEnteredOptionApi();
		} else {
			history.push(CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_ENTEREDTYPE_PATH(TRANSFERIN_ENTEREDTYPE.UNENTERED));
		}
		if (resp) {
			checkPeopleType(resp);
			setOptions(resp);
			setMounted(true);
		}
	};

	// 檢查健康申告書
	const doCheckHealthDeclarationApi = async (peopleType, referenceID) => {
		return referenceID > 0 && (await getDocHealthDeclarationIsValidApi({ type: peopleType, id: referenceID }));
	};

	const useSearchBarMemo = useMemo(
		() => {
			return <SearchBar ref={searchRef} className="mb-4" TabsComponentProps={<MembershipTransferInSearchBarTabs />} sourceOption={options.targetOptions} onResetEvent={handleResetClick} onCreateEvent={handleCreateClick} onSearchHistoryEvent={handleSearchHistoryClick} routerPath={CLUB_DOCUMENTS_ROUTER.TRANSFERIN.GET_ENTEREDTYPE_PATH(enteredTypeProps)} />;
		},
		// eslint-disable-next-line
		[options, enteredTypeProps]
	);

	useEffect(
		() => {
			doInitOptionsApi();
			return () => setMounted(false);
		},
		// eslint-disable-next-line
		[enteredTypeProps]
	);

	// peopleTypeProps is 14  必為閱讀對象為已移入只能看不能改
	return (
		<Box className="container main-container-spacing">
			{isMounted && (
				<React.Fragment>
					<Box component="header" className="page-header">
						<Typography className="page-header-title" variant="h3">
							國內移籍申請書 (移入)
						</Typography>
						<Box className="btn-group">
							<BlankFormBox sourceData={DOC_MEMBERSHIP_TRANSFER_IN_LINK} />
						</Box>
					</Box>
					{useSearchBarMemo}
					<Switch>
						<PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.TRANSFERIN.HISTORY} as={MembershipTransferInHistory} isReadOnly={isEqual(peopleTypeProps, PEOPLE_TYPE.ALREADYTRANSFERINMEMBER)} onBlankContractClick={handleCreateClick} searchRef={searchRef} />
						<PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.TRANSFERIN.CONTRACT_UNENTERED} as={MembershipTransferInContent} onReturnHistory={handleReturnHistory} />
						<PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.TRANSFERIN.CONTRACT_ENTERED} as={MembershipTransferInContent} onReturnHistory={handleReturnHistory} />
						<PrivateRoute exact path={CLUB_DOCUMENTS_ROUTER.TRANSFERIN.READONLY_ENTERED} as={MembershipTransferInContent} onReturnHistory={handleReturnHistory} isReadOnly={true} />
					</Switch>
					<ConfirmTemporaryContractDialog open={isEqual(openTarget, DIALOGTYPE.TEMPORARY)} handleClose={handleCloseDialog} handleClickYes={handleDialogYseClick} handleClickNo={handleDialogNoClick} />
					<ConfirmHealthDeclarationNotifyDialog open={isEqual(openTarget, DIALOGTYPE.HEALTHDECLARATIONNOTIFY)} handleClose={handleCloseDialog} handleClickYes={handleGoToHealthDeclarationPage} handleClickNo={handleCloseDialog} />
				</React.Fragment>
			)}
		</Box>
	);
};

export default MembershipTransferInPage;
